import { useState } from "react";
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatch,
  useNavigate,
} from "@tanstack/react-router";
import { SidebarInset, SidebarProvider } from "@/shared/ui/sidebar";
import { AppSidebar } from "@/widgets/app-sidebar/ui/app-sidebar";
import { useUnsavedViews } from "@/pages/use-unsaved-views";
import { UnsavedViewsDialog } from "@/pages/unsaved-views-dialog";
import { TaskController } from "@/features/task-controller/ui/task-controller";
import { SuspenseAndErrorBoundary } from "@/shared/ui/suspense-and-error-boundary";
import { MarkerIOAuthHandler } from "@/features/marker-io-auth/ui/marker-io-auth-handler";
import { HubSpotProvider, useSupportChat } from "@/features/hubspot-chat";
import { OutOfCreditsDialog } from "@/features/out-of-credits-dialog";
import { useIsSuperAdmin } from "@/entities/me/hooks/use-is-super-admin";
import { AppSkeleton } from "@/shared/ui/app-skeleton";
import { PageSkeleton } from "@/shared/ui/page-skeleton";
import {
  ONBOARDING_ROUTE,
  SUBSCRIPTION_SETUP_ROUTE,
  SUBSCRIPTION_OVERDUE_ROUTE,
} from "@/shared/lib/routes";
import { SubscriptionStatus } from "@/shared/gql/graphql";
import { GlobalSearchBar } from "@/widgets/global-search/ui/global-search-bar";
import { OverdueInvoiceBanner } from "@/widgets/overdue-invoice-banner";
import { TrialBanner } from "@/widgets/trial-banner";
import { resetCacheOnScopeChange } from "@/shared/gql/apollo-client";
import { syncAnalyticsCompany } from "@/shared/lib/analytics";

const OutOfCreditsDialogHost = () => {
  const { openSupportChat } = useSupportChat();
  const navigate = useNavigate();
  const { companySlug } = Route.useParams();
  const isSuperAdmin = useIsSuperAdmin();

  return (
    <OutOfCreditsDialog
      onChatWithSupport={openSupportChat}
      onViewAiCreditPacks={
        isSuperAdmin
          ? () =>
              navigate({
                to: "/$companySlug/settings/subscription",
                params: { companySlug },
                search: { addons: "ai" },
              })
          : undefined
      }
    />
  );
};

// The profile's unsaved-views dot, menu item and dialog (DEV-591). The
// dialog sits outside the sidebar so it stays open after the menu closes.
const AppSidebarHost = () => {
  const unsavedViews = useUnsavedViews();
  const [isUnsavedViewsOpen, setIsUnsavedViewsOpen] = useState(false);

  return (
    <>
      <AppSidebar
        unsavedViewCount={unsavedViews.count}
        onOpenUnsavedViews={() => setIsUnsavedViewsOpen(true)}
      />
      <UnsavedViewsDialog
        open={isUnsavedViewsOpen}
        onOpenChange={setIsUnsavedViewsOpen}
        unsavedViews={unsavedViews}
      />
    </>
  );
};

const Layout = () => {
  const isAdminRoute = useMatch({
    from: "/_authed/$companySlug/admin",
    shouldThrow: false,
  });

  if (isAdminRoute) {
    return <Outlet />;
  }

  return (
    <HubSpotProvider>
      <SuspenseAndErrorBoundary fallback={<AppSkeleton />}>
        <SuspenseAndErrorBoundary>
          <MarkerIOAuthHandler />
        </SuspenseAndErrorBoundary>
        <SuspenseAndErrorBoundary>
          <TrialBanner />
        </SuspenseAndErrorBoundary>
        <SidebarProvider>
          <AppSidebarHost />
          <SidebarInset>
            <SuspenseAndErrorBoundary>
              <OverdueInvoiceBanner />
            </SuspenseAndErrorBoundary>
            <SuspenseAndErrorBoundary fallback={<PageSkeleton />}>
              <Outlet />
            </SuspenseAndErrorBoundary>
            <SuspenseAndErrorBoundary>
              <TaskController />
            </SuspenseAndErrorBoundary>
          </SidebarInset>
        </SidebarProvider>
      </SuspenseAndErrorBoundary>
      <GlobalSearchBar />
      <OutOfCreditsDialogHost />
    </HubSpotProvider>
  );
};

import { taskSearchSchema } from "@/features/task-controller/model/search-schema";

export const Route = createFileRoute("/_authed/$companySlug")({
  component: Layout,
  validateSearch: taskSearchSchema,
  beforeLoad: ({ context, params, preload }) => {
    const company = context.me?.companies.find(
      (c) => c.slug === params.companySlug,
    );
    if (!company) throw redirect({ to: "/" });
    // Before the onboarding/subscription redirects, so those funnels carry the company
    // too. Not on a preload: hovering a link must not switch the active company.
    if (!preload) syncAnalyticsCompany(company);
    const isSuperAdmin = context.me?.isSuperAdmin ?? false;
    if (!company.onboardingCompletedAt && !isSuperAdmin) {
      throw redirect({ to: ONBOARDING_ROUTE });
    }
    // hasPlatformAccess also covers a past-due company a super admin granted payment grace.
    if (!company.hasPlatformAccess && !isSuperAdmin) {
      // Overdue clients keep an existing (past-due) subscription — send them to
      // settle the outstanding invoice, not to buy a new plan.
      const to =
        company.subscriptionStatus === SubscriptionStatus.PastDue
          ? SUBSCRIPTION_OVERDUE_ROUTE
          : SUBSCRIPTION_SETUP_ROUTE;
      throw redirect({
        to,
        params: { companySlug: params.companySlug },
      });
    }
    // Only once the navigation is committing, so a redirect above doesn't drop a cache for a
    // company the user never lands on. Mirrored in _noSidebar/$companySlug, the other route
    // tree that serves company data.
    resetCacheOnScopeChange(company.slug);
    return { company };
  },
});
